import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Folder, Merge } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function Group({ g, i, onMerge }) {
  const [keep, setKeep] = useState(g.folders[0].id);
  const kept = g.folders.find((f) => f.id === keep);
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid={`folder-dup-group-${i}`}>
      <div className="text-xs text-slate-500">Dans : <span className="font-semibold text-slate-700">{g.path}</span></div>
      <div className="space-y-1.5">
        {g.folders.map((f) => (
          <label key={f.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer ${keep === f.id ? "border-[#1E3A8A] bg-blue-50" : ""}`}>
            <input type="radio" name={`keep-${i}`} checked={keep === f.id} onChange={() => setKeep(f.id)} data-testid={`folder-dup-keep-${i}-${f.id}`} />
            <Folder className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium flex-1 truncate">{f.name}</span>
            <span className="text-xs text-slate-500">{f.files} fichier(s) · {f.subfolders} sous-dossier(s)</span>
          </label>
        ))}
      </div>
      <Button size="sm" onClick={() => onMerge(g, kept)} data-testid={`folder-dup-merge-${i}`}><Merge className="h-4 w-4 mr-1.5" />Tout regrouper dans « {kept.name} »</Button>
    </div>
  );
}

export function AdminFolderMerge() {
  const [groups, setGroups] = useState(null);
  const [pending, setPending] = useState(null);
  const load = () => api.get("/admin/folder-duplicates").then((r) => setGroups(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const merge = async () => {
    const { g, kept } = pending;
    try {
      const { data } = await api.post("/admin/folder-duplicates/merge", { keep_id: kept.id, merge_ids: g.folders.filter((f) => f.id !== kept.id).map((f) => f.id) });
      toast.success(`Dossiers fusionnés : ${data.files_moved} fichier(s) déplacé(s)`);
      setPending(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!groups) return null;
  return (
    <div className="space-y-3" data-testid="folder-duplicates">
      <h3 className="font-display font-bold">Dossiers en double</h3>
      <p className="text-sm text-slate-600">Dossiers au même endroit dont le nom désigne le même fournisseur (majuscules, forme juridique ou parenthèses près). Choisissez le nom à garder : le contenu des autres y est regroupé, sous-dossiers compris, puis les dossiers vides vont à la corbeille.</p>
      {groups.length === 0
        ? <div className="border border-dashed rounded-xl py-8 text-center text-sm text-slate-500" data-testid="folder-duplicates-empty">Aucun dossier en double.</div>
        : groups.map((g, i) => <Group key={g.folders.map((f) => f.id).join()} g={g} i={i} onMerge={(gg, kept) => setPending({ g: gg, kept })} />)}
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fusionner ces dossiers ?</AlertDialogTitle>
            <AlertDialogDescription>Tout le contenu sera regroupé dans « {pending?.kept.name} ». Les droits d'accès des dossiers fusionnés sont conservés sur ce dossier.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={merge} data-testid="folder-merge-confirm">Fusionner</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
